import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { ConfirmModal } from '../../components/Common/ConfirmModal';

interface DiscoveredReport {
    id: number;
    source: string;
    source_url: string;
    title?: string | null;
    societe_symbole?: string | null;
    publication_date?: string | null;
    discovered_at?: string | null;
    statut: string;
    import_result_json?: string | null;
    imported_at?: string | null;
    exercice_detecte?: number | null;
    period_type_detecte?: string | null;
    deja_en_base?: boolean;
}

interface Stock {
    symbole: string;
    nom_societe?: string;
}

interface ImportResult {
    status?: string;
    extracted?: number;
    inserted?: number;
    dividende_upserted?: boolean;
    sanity_warnings?: string[];
    error?: string;
    articleGenerated?: boolean;
    periode_document?: string | null;
    conflits?: number;
    quarantaine?: string[];
    alertes?: string[];
    journal_ids?: number[];
}

const STATUT_STYLES: Record<string, { bg: string; fg: string; border: string; label: string }> = {
    NOUVEAU: { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd', label: 'Nouveau' },
    IMPORTE: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7', label: 'Importé' },
    IGNORE: { bg: '#f1f5f9', fg: '#64748b', border: '#cbd5e1', label: 'Ignoré' },
    ERREUR: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5', label: 'Erreur' },
    QUARANTAINE: { bg: '#ffedd5', fg: '#9a3412', border: '#fdba74', label: 'Quarantaine' },
};

const StatutBadge: React.FC<{ statut: string }> = ({ statut }) => {
    const style = STATUT_STYLES[statut] || STATUT_STYLES.NOUVEAU;
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            backgroundColor: style.bg,
            color: style.fg,
            border: `1px solid ${style.border}`,
        }}>
            {style.label}
        </span>
    );
};

const inputStyle: React.CSSProperties = {
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    backgroundColor: 'var(--bg-input, white)',
};

const AdminDiscoveredReportsScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [reports, setReports] = useState<DiscoveredReport[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [loading, setLoading] = useState(false);
    const [statutFilter, setStatutFilter] = useState('NOUVEAU');
    const [forcerId, setForcerId] = useState<number | null>(null);
    const [societeFilter, setSocieteFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [fundamentalsMatch, setFundamentalsMatch] = useState('');

    const [scanLoading, setScanLoading] = useState(false);
    const [scanMessage, setScanMessage] = useState('');

    const [editingSymboleId, setEditingSymboleId] = useState<number | null>(null);
    const [editingSymboleValue, setEditingSymboleValue] = useState('');

    const [importingId, setImportingId] = useState<number | null>(null);
    const [generatingArticleId, setGeneratingArticleId] = useState<number | null>(null);
    const [importResults, setImportResults] = useState<Record<number, ImportResult>>({});
    const [importErrors, setImportErrors] = useState<Record<number, string>>({});

    const fetchStocks = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/stocks/active`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setStocks(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching stocks:', error);
        }
    }, [token]);

    const fetchReports = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statutFilter) params.set('statut', statutFilter);
            if (societeFilter) params.set('societe_symbole', societeFilter);
            if (sourceFilter) params.set('source', sourceFilter);
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            if (fundamentalsMatch) params.set('fundamentals_match', fundamentalsMatch);
            const qs = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_URL}/v1/admin/discovered-reports${qs}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setReports(Array.isArray(data) ? data : []);
            } else {
                setReports([]);
            }
        } catch (error) {
            console.error('Error fetching discovered reports:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    }, [token, statutFilter, societeFilter, sourceFilter, dateFrom, dateTo, fundamentalsMatch]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchStocks();
            fetchReports();
        }
    }, [token, hasPermission, fetchStocks, fetchReports]);

    const handleScan = async (source: 'richbourse' | 'brvm' | 'boc') => {
        setScanLoading(true);
        setScanMessage('');
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-reports/scan?source=${source}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                if (source === 'brvm') {
                    setScanMessage(data.detail || 'Scan brvm.org lancé en arrière-plan.');
                } else if (source === 'boc') {
                    setScanMessage(`Scan BOC terminé : ${data.scanned ?? '?'} rapport(s) vu(s), ${data.new ?? '?'} nouveau(x) en cours d'import.`);
                    fetchReports();
                } else {
                    setScanMessage(`Scan terminé : ${data.scanned ?? '?'} rapport(s) vu(s), ${data.new ?? '?'} nouveau(x).`);
                    fetchReports();
                }
            } else {
                setScanMessage((data as { detail?: string }).detail || 'Erreur lors du scan.');
            }
        } catch (error) {
            setScanMessage('Erreur réseau lors du scan.');
        } finally {
            setScanLoading(false);
        }
    };

    const handleStartEditSymbole = (report: DiscoveredReport) => {
        setEditingSymboleId(report.id);
        setEditingSymboleValue(report.societe_symbole || '');
    };

    const handleSaveSymbole = async (reportId: number) => {
        if (!editingSymboleValue) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-reports/${reportId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ societe_symbole: editingSymboleValue }),
            });
            if (response.ok) {
                setEditingSymboleId(null);
                fetchReports();
            } else {
                const err = await response.json().catch(() => ({}));
                setImportErrors(prev => ({ ...prev, [reportId]: (err as { detail?: string }).detail || 'La société n’a pas pu être corrigée.' }));
            }
        } catch (error) {
            setImportErrors(prev => ({ ...prev, [reportId]: 'Erreur réseau : la société n’a pas pu être corrigée.' }));
        }
    };

    const handleImport = async (reportId: number, forcer = false) => {
        setImportingId(reportId);
        setImportErrors(prev => ({ ...prev, [reportId]: '' }));
        try {
            const url = `${API_URL}/v1/admin/discovered-reports/${reportId}/import${forcer ? '?forcer=true' : ''}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setImportResults(prev => ({ ...prev, [reportId]: data }));
            } else {
                setImportErrors(prev => ({ ...prev, [reportId]: (data as { detail?: string }).detail || 'Échec de l\'import.' }));
            }
            fetchReports();
        } catch (error) {
            setImportErrors(prev => ({ ...prev, [reportId]: 'Erreur réseau.' }));
        } finally {
            setImportingId(null);
        }
    };

    const handleDismiss = async (reportId: number) => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-reports/${reportId}/dismiss`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                fetchReports();
            }
        } catch (error) {
            console.error('Error dismissing report:', error);
        }
    };

    const handleGenerateArticle = async (reportId: number) => {
        setGeneratingArticleId(reportId);
        setImportErrors(prev => ({ ...prev, [reportId]: '' }));
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-reports/${reportId}/generate-article`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setImportResults(prev => ({ ...prev, [reportId]: { ...prev[reportId], articleGenerated: true } }));
            } else {
                setImportErrors(prev => ({ ...prev, [reportId]: (data as { detail?: string }).detail || "Échec de la génération de l'article." }));
            }
        } catch (error) {
            setImportErrors(prev => ({ ...prev, [reportId]: 'Erreur réseau.' }));
        } finally {
            setGeneratingArticleId(null);
        }
    };

    const rapportAForcer = reports.find(r => r.id === forcerId);

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé.</p></Box>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon='travel_explore' title='Rapports Détectés' />

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Découverte de rapports financiers (brvm.org + richbourse.com)</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                        brvm.org (source officielle) est scanné automatiquement chaque jour à 05h00 UTC.
                        richbourse.com reste manuel (secondaire / vérification croisée). Import volontairement
                        manuel dans les deux cas — aucune publication automatique. Vérifiez/corrigez la société
                        détectée puis cliquez "Importer" par ligne.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <button
                            onClick={() => handleScan('boc')}
                            disabled={scanLoading}
                            title="Scanne les Bulletins Officiels de la Cote récents (signets natifs) et extrait les rapports financiers directement."
                            style={{ backgroundColor: '#059669', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            {scanLoading ? 'Scan BOC...' : '⚡ Scanner les BOC (Direct)'}
                        </button>

                        <button
                            onClick={() => handleScan('brvm')}
                            disabled={scanLoading}
                            title="brvm.org respecte un Crawl-delay de 10s entre requêtes — le scan complet prend plusieurs minutes et tourne en arrière-plan."
                            style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            {scanLoading ? 'Lancement...' : 'Scanner brvm.org maintenant'}
                        </button>

                        <button
                            onClick={() => handleScan('richbourse')}
                            disabled={scanLoading}
                            style={{ backgroundColor: '#64748b', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            {scanLoading ? 'Scan en cours...' : 'Scanner richbourse (secondaire)'}
                        </button>

                        <button
                            onClick={() => fetchReports()}
                            disabled={loading}
                            title="Le scan brvm.org tourne en arrière-plan (plusieurs minutes) — la liste ne se rafraîchit pas toute seule, cliquez ici pour vérifier l'avancement."
                            style={{ backgroundColor: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            🔄 Actualiser la liste
                        </button>

                        {scanMessage && <span style={{ fontSize: '13px', color: '#475569' }}>{scanMessage}</span>}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <select
                            value={statutFilter}
                            onChange={e => setStatutFilter(e.target.value)}
                            style={inputStyle}
                        >
                            <option value="">Tous les statuts</option>
                            <option value="NOUVEAU">Nouveau</option>
                            <option value="IMPORTE">Importé</option>
                            <option value="ERREUR">Erreur</option>
                            <option value="QUARANTAINE">Quarantaine</option>
                            <option value="IGNORE">Ignoré</option>
                        </select>

                        <select
                            value={societeFilter}
                            onChange={e => setSocieteFilter(e.target.value)}
                            style={inputStyle}
                        >
                            <option value="">Toutes les sociétés</option>
                            {stocks.map(s => <option key={s.symbole} value={s.symbole}>{s.symbole} - {s.nom_societe || ''}</option>)}
                        </select>

                        <select
                            value={sourceFilter}
                            onChange={e => setSourceFilter(e.target.value)}
                            style={inputStyle}
                        >
                            <option value="">Toutes les sources</option>
                            <option value="boc">BOC</option>
                            <option value="brvm">BRVM</option>
                            <option value="richbourse">Richbourse</option>
                        </select>

                        <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Du
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                        </label>
                        <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Au
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
                        </label>

                        <select
                            value={fundamentalsMatch}
                            onChange={e => setFundamentalsMatch(e.target.value)}
                            style={inputStyle}
                            title="Rapprochement déterministe (année + période déduites du titre) avec les données déjà en base — un indice, pas une certitude"
                        >
                            <option value="">Correspondance fundamentals : toutes</option>
                            <option value="unmatched">Pas encore en base (à examiner)</option>
                            <option value="matched">Déjà en base</option>
                        </select>

                        {(societeFilter || sourceFilter || dateFrom || dateTo || fundamentalsMatch) && (
                            <button
                                onClick={() => { setSocieteFilter(''); setSourceFilter(''); setDateFrom(''); setDateTo(''); setFundamentalsMatch(''); }}
                                style={{ backgroundColor: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', padding: '9px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                            >
                                Réinitialiser les filtres
                            </button>
                        )}
                    </div>

                    {reports.length >= 100 && (
                        <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>
                            Affichage limité aux 100 rapports les plus récents pour ce filtre — filtrez par statut pour affiner.
                        </p>
                    )}

                    {loading ? (
                        <p>Chargement...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Date publication</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Société</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Titre</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Statut</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                Aucun rapport détecté pour ce filtre — lancez un scan.
                                            </td>
                                        </tr>
                                    ) : reports.map((r, idx) => (
                                        <React.Fragment key={r.id}>
                                            <tr style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{r.publication_date ?? '—'}</td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                                    {editingSymboleId === r.id ? (
                                                        <div style={{ display: 'flex', gap: '6px' }}>
                                                            <select value={editingSymboleValue} onChange={e => setEditingSymboleValue(e.target.value)} style={inputStyle}>
                                                                <option value="">-- Sélectionner --</option>
                                                                {stocks.map(s => <option key={s.symbole} value={s.symbole}>{s.symbole} - {s.nom_societe || ''}</option>)}
                                                            </select>
                                                            <button onClick={() => handleSaveSymbole(r.id)} className='button button-purple' style={{ padding: '4px 10px', fontSize: '12px' }}>OK</button>
                                                            <button onClick={() => setEditingSymboleId(null)} className='button' style={{ padding: '4px 10px', fontSize: '12px' }}>Annuler</button>
                                                        </div>
                                                    ) : (
                                                        <span
                                                            onClick={() => handleStartEditSymbole(r)}
                                                            style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                            title="Cliquer pour corriger"
                                                        >
                                                            {r.societe_symbole ?? <span style={{ color: '#ef4444', fontStyle: 'italic' }}>Non détectée</span>}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                                    <span style={{
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        color: r.source === 'boc' ? '#059669' : r.source === 'brvm' ? '#4f46e5' : '#94a3b8',
                                                        marginRight: '6px'
                                                    }}>
                                                        {r.source === 'boc' ? 'BOC' : r.source === 'brvm' ? 'BRVM' : 'RICHBOURSE'}
                                                    </span>
                                                    <a href={r.source_url} target='_blank' rel='noreferrer'>{r.title ?? r.source_url}</a>
                                                    {r.deja_en_base && (
                                                        <span
                                                            title={`Une ligne fundamentals existe déjà pour ${r.period_type_detecte ?? ''} ${r.exercice_detecte ?? ''} — indice de doublon probable, pas une certitude`}
                                                            style={{ marginLeft: '8px', display: 'inline-block', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
                                                        >
                                                            Déjà en base
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <StatutBadge statut={r.statut} />
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => handleImport(r.id)}
                                                        disabled={importingId === r.id || !r.societe_symbole}
                                                        title={!r.societe_symbole ? 'Renseignez la société avant import' : ''}
                                                        style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px', opacity: !r.societe_symbole ? 0.5 : 1 }}
                                                    >
                                                        {importingId === r.id ? 'Import...' : 'Importer'}
                                                    </button>
                                                    {r.statut === 'IMPORTE' && (
                                                        <button
                                                            onClick={() => handleGenerateArticle(r.id)}
                                                            disabled={generatingArticleId === r.id}
                                                            title="Génère (ou régénère) l'article IA proposé pour ce rapport — normalement automatique à l'import, à utiliser en cas d'échec ou pour régénérer après correction des données."
                                                            style={{ backgroundColor: '#0ea5e9', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px' }}
                                                        >
                                                            {generatingArticleId === r.id ? 'Génération...' : "Générer l'article"}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDismiss(r.id)}
                                                        style={{ backgroundColor: '#64748b', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
                                                    >
                                                        Ignorer
                                                    </button>
                                                </td>
                                            </tr>
                                            {(importResults[r.id] || importErrors[r.id]) && (
                                                <tr>
                                                    <td colSpan={5} style={{ padding: '8px 16px 16px 16px', fontSize: '12px' }}>
                                                        {importErrors[r.id] && (
                                                            <div style={{ color: '#991b1b', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '8px 12px' }}>
                                                                ❌ {importErrors[r.id]}
                                                            </div>
                                                        )}
                                                        {importResults[r.id] && (importResults[r.id].quarantaine || []).length > 0 && (
                                                            <div style={{ color: '#9a3412', backgroundColor: '#ffedd5', border: '1px solid #fdba74', borderRadius: '6px', padding: '8px 12px', marginBottom: '6px' }}>
                                                                Import mis en quarantaine : rien n’a été écrit. Une incohérence apparaîtrait avec les données déjà en base.
                                                                {(importResults[r.id].alertes || []).filter(a => a.startsWith('BLOQUANT')).map((a, i) => (
                                                                    <div key={i} style={{ marginTop: '4px' }}>• {a.replace(/^BLOQUANT : /, '')}</div>
                                                                ))}
                                                                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                                    <a href='/admin/journal-imports'>Voir le journal des imports</a>
                                                                    <button
                                                                        onClick={() => setForcerId(r.id)}
                                                                        style={{ backgroundColor: '#c2410c', color: 'white', border: 'none', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                                                                    >
                                                                        Forcer l’import
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {importResults[r.id] && importResults[r.id].inserted !== undefined && !(importResults[r.id].quarantaine || []).length && (
                                                            <div style={{ color: '#065f46', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '6px', padding: '8px 12px' }}>
                                                                ✅ {importResults[r.id].inserted ?? 0} ligne(s) mise(s) à jour
                                                                {importResults[r.id].periode_document ? ` • période du document : ${importResults[r.id].periode_document}` : ' • période du document indéterminée : complément seulement'}
                                                                {(importResults[r.id].conflits ?? 0) > 0 ? ` • ${importResults[r.id].conflits} conflit(s) non écrit(s), à revoir dans le journal des imports` : ''}
                                                                {importResults[r.id].dividende_upserted ? ' • dividende mis à jour' : ''}
                                                                {(importResults[r.id].sanity_warnings || []).length > 0 && (
                                                                    <div style={{ marginTop: '6px', color: '#92400e' }}>
                                                                        {(importResults[r.id].sanity_warnings || []).map((w, i) => (
                                                                            <div key={i}>⚠️ {w}</div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {importResults[r.id]?.articleGenerated && (
                                                            <div style={{ marginTop: '6px', color: '#065f46', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '6px', padding: '8px 12px' }}>
                                                                ✅ Article généré — à relire dans <a href="/admin/proposed-articles">Articles Proposés</a>.
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>
            <ConfirmModal
                isOpen={forcerId !== null}
                title='Forcer l’import malgré une incohérence'
                message={`Les chiffres extraits de « ${rapportAForcer?.title ?? 'ce rapport'} » seront écrits en base alors qu’ils créent une incohérence avec les données existantes (cumul qui décroît, deux périodes identiques, capitaux propres égaux au résultat…). Ne forcez qu’après avoir relu le rapport source : l’écriture est consignée dans le journal des imports.`}
                confirmLabel='Forcer l’import'
                isDestructive
                isLoading={forcerId !== null && importingId === forcerId}
                onConfirm={() => {
                    const id = forcerId;
                    setForcerId(null);
                    if (id !== null) handleImport(id, true);
                }}
                onCancel={() => setForcerId(null)}
            />
        </SiteLayout>
    );
};

export default AdminDiscoveredReportsScreen;
